// src/hooks/useMenuItems.js

import { useState, useEffect } from 'react';
import { itemService } from '../services/menuitemService';
import { toast } from 'react-hot-toast';


export const useMenuItems = (initialCategoryId = null) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryId, setCategoryId] = useState(initialCategoryId);

  const processItems = (rawItems) => {
  if (!Array.isArray(rawItems)) {
    console.warn('Invalid menu items data:', rawItems);
    return [];
  }

  return rawItems.map((item) => {
    return {
      id: item.id,
      name: item.name,
      description: item.description || '',
      basePrice: parseFloat(item.basePrice) || 0,
      price: parseFloat(item.basePrice) || 0, // Alias for compatibility
      categoryId: item.categoryId,
      category: item.categoryName || 'Uncategorized',
      isActive: item.isActive ?? true,
      variants: item.variants || [], // ✅ Include variants array
      isVeg: item.isVeg ?? true,
      isFavourite: item.isFavourite ?? false,
      isCustomizable: item.isCustomizable ?? false,
      preparationTime: item.preparationTime || 0,
      kitchenStationId: item.kitchenStationId,
      kitchenStationName: item.kitchenStationName,
      shortcutKey: item.shortcutKey,
      // New optional fields 
      itemCode: item.itemCode || null,
      barcode: item.barcode || null,
      // ✅ Tax information from API
      effectiveTaxId: item.effectiveTaxId,
      effectiveTaxName: item.effectiveTaxName,
      effectiveTaxRate: parseFloat(item.effectiveTaxRate) || 0,
      effectiveTaxPercentage: parseFloat(item.effectiveTaxRate) || 0, // Alias
      effectiveTaxCgstRate: parseFloat(item.effectiveTaxCgstRate) || 0,
      effectiveTaxSgstRate: parseFloat(item.effectiveTaxSgstRate) || 0,
      effectiveInclusiveTaxRate: item.effectiveInclusiveTaxRate !== false,
      // Open item flags
      isOpenItem: item.isOpenItem ?? false,
      isNameEditable: item.isNameEditable ?? false,
       markedAsToppings: item.markedAsToppings ?? false,
    };
  });
};
 
  const fetchItems = async () => {
    setLoading(true);
    setError(null);

    try {
      let response;

      if (categoryId) {
        response = await itemService.getByCategory(categoryId);
      } else {
        response = await itemService.getAll();
      }

      const rawItems = response?.data?.data || response?.data || response || [];

      const processedItems = processItems(rawItems);

      setItems(processedItems);
      setLoading(false);
    } catch (err) {
      console.error('❌ Failed to fetch menu items:', err);
      setError(err.message || 'Failed to load menu items');
      setLoading(false);
      toast.error('Failed to fetch menu items');
    }
  };

  const refreshItems = async () => {
    await fetchItems();
    toast.success('Menu items refreshed', { duration: 2000 });
  };

  const createItem = async (itemData) => {
    try {
      const response = await itemService.create(itemData);
      toast.success('Item created successfully');
      await fetchItems(); // refresh after creation
      return response;
    } catch (err) {
      console.error('❌ Error creating item:', err);
      toast.error('Failed to create item');
      throw err;
    }
  };

  const updateItem = async (id, updates) => {
    try {
      const response = await itemService.update(id, updates);
      toast.success('Item updated successfully');
      await fetchItems(); // refresh after update
      return response;
    } catch (err) {
      console.error('❌ Error updating item:', err);
      toast.error('Failed to update item');
      throw err;
    }
  };

  const deleteItem = async (id) => {
    try {
      await itemService.remove(id);
      toast.success('Item deleted successfully');
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      console.error('❌ Error deleting item:', err);
      toast.error('Failed to delete item');
      throw err;
    }
  };


  useEffect(() => {
    fetchItems();
  }, [categoryId]);

  return {
    items,
    loading,
    error,
    categoryId,
    setCategoryId,
    fetchItems,
    refreshItems,
    createItem,
    updateItem,
    deleteItem,
  };
};

export default useMenuItems;
